import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService, Toast, ToastType } from '../../../core/services/toast.service';

/**
 * Toast Container Component
 *
 * Renders toast notifications in a fixed position container.
 * Uses Bootstrap 5 toast styling with custom enhancements.
 *
 * Place this component once in the app layout:
 * ```html
 * <app-toast-container></app-toast-container>
 * ```
 */
@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toast-container.component.html',
  styleUrls: ['./toast-container.component.scss']
})
export class ToastContainerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  toasts: Toast[] = [];

  constructor(private toastService: ToastService) {}

  ngOnInit(): void {
    this.toastService.toasts$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(toasts => {
      this.toasts = toasts;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Dismiss a toast
   */
  dismiss(toast: Toast): void {
    this.toastService.dismiss(toast.id);
  }

  /**
   * Handle action button click
   */
  onActionClick(toast: Toast): void {
    if (toast.action?.callback) {
      toast.action.callback();
    }
    this.dismiss(toast);
  }

  /**
   * Get Bootstrap background class for toast type
   */
  getToastClass(type: ToastType): string {
    switch (type) {
      case 'success': return 'toast-success';
      case 'error': return 'toast-error';
      case 'warning': return 'toast-warning';
      case 'info': return 'toast-info';
    }
  }

  /**
   * Get text color class based on toast type
   */
  getTextClass(type: ToastType): string {
    // Dark backgrounds need light text
    return type === 'warning' ? 'text-dark' : 'text-white';
  }

  /**
   * Track by function for ngFor
   */
  trackByToast(index: number, toast: Toast): string {
    return toast.id;
  }
}
